<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <p style="margin-top: -20px; font-size: 12px; position: relative; z-index: 1;">
            <i>CRB Reports are external references to a person's credit history & financial behavior.
                <span class="text-primary fw-medium learn-more-link" @click="$bvModal.show('crb-info-modal')">
                    Learn More
                </span>
            </i>
        </p>

        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <div class="text-end mb-3">
                            <button class="btn btn-sm btn-primary fw-bold"
                                @click="$router.push('/new-crb-report')">
                                <i class="mdi mdi-plus"></i> Generate New CRB Report
                            </button>
                        </div>

                        <div class="row mb-2">
                            <div class="col-sm-12 col-md-6">
                                <div id="tickets-table_length" class="dataTables_length">
                                    <label class="d-inline-flex align-items-center">
                                        Show
                                        <b-form-select class="form-select form-select-sm mx-1" v-model="perPage"
                                            size="sm" :options="pageOptions"></b-form-select> entries
                                    </label>
                                </div>
                            </div>
                            <div class="col-sm-12 col-md-6">
                                <div id="tickets-table_filter" class="dataTables_filter text-md-end">
                                    <label class="d-inline-flex align-items-center">
                                        Search:
                                        <b-form-input v-model="filter" type="search" placeholder="Search..."
                                            class="form-control form-control-sm ms-2"></b-form-input>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div class="table-responsive">
                            <b-table :items="crbreports" :fields="fields" responsive="sm" :per-page="perPage"
                                :current-page="currentPage" :filter="filter" :busy="isBusy" show-empty>
                                <template #table-busy>
                                    <div class="text-center text-primary my-2">
                                        <b-spinner class="align-middle" type="grow" small></b-spinner>
                                        <strong> Fetching Data...</strong>
                                    </div>
                                </template>

                                <template #empty>
                                    <div class="text-center text-muted my-2">
                                        <i class="fs-5 mdi mdi-cancel"></i>
                                        <strong> No CRB Reports Found</strong>
                                    </div>
                                </template>

                                <template #cell(index)="data">
                                    {{ data.index + 1 }}
                                </template>

                                <template #cell(member_name)="data">
                                    <router-link :to="{
                                        path: '/member-profile',
                                        query: { id: data.item.member_id },
                                    }">
                                        <span @mouseover="name_hover = true; current_index = data.index"
                                            @mouseout="name_hover = false">
                                            <span
                                                :class="(name_hover && current_index == data.index) ? 'fw-bold text-dark text-decoration-underline' : 'text-dark'">{{
                                                    data.item.member_name }}</span>
                                            <span class="d-block text-info fw-bold">{{ data.item.member_id
                                                }}</span>
                                        </span>
                                    </router-link>
                                </template>



                                <template #cell(report_type)="data">
                                    <span class="badge bg-primary p-1"
                                        v-if="data.item.report_type === 'STANDARD'">Standard</span>
                                    <span class="badge bg-info p-1"
                                        v-else-if="data.item.report_type === 'COMPACT'">Compact</span>
                                    <span class="badge bg-dark p-1"
                                        v-else-if="data.item.report_type === 'ENHANCED'">Enhanced</span>
                                </template>

                                <template #cell(actions)="data">
                                    <button @click.prevent="promptViewReport(data.item)" class="btn btn-sm btn-primary"
                                        title="View Full Report">
                                        <i class="mdi mdi-eye"></i> View
                                    </button>
                                </template>
                            </b-table>
                        </div>

                        <div class="row">
                            <div class="col">
                                <div class="dataTables_paginate paging_simple_numbers float-end">
                                    <ul class="pagination pagination-rounded mb-0">
                                        <b-pagination v-model="currentPage" :total-rows="totalRows"
                                            :per-page="perPage"></b-pagination>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Token Charge Warning Modal -->
        <b-modal id="token-warning-modal" centered hide-header hide-footer>
            <div class="text-center p-3">
                <div><i class="display-3 mdi mdi-alert-circle-outline text-warning"></i></div>
                <h4 class="mt-2">Token Charge Warning</h4>
                <p class="mt-3">
                    Retrieving the CRB Report <strong>{{ selectedReport ? selectedReport.reference : '' }}</strong>
                    will
                    consume a token from your balance.
                </p>
                <p>Are you sure you want to proceed?</p>
                <div class="mt-4">
                    <b-button class="mx-1" variant="primary" @click="proceedToViewReport">
                        Yes, Proceed
                    </b-button>
                    <b-button variant="secondary" @click="$bvModal.hide('token-warning-modal')">Cancel</b-button>
                </div>
            </div>
        </b-modal>

        <CrbInfoModal />

    </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import CrbInfoModal from "./crb_info_modal.vue";
import { crbReportsComputed, crbReportsMethods } from "@/state/helpers"; //[cite: 112]

export default {
    components: {
        Layout,
        PageHeader,
        CrbInfoModal
    },
    data() {
        return {
            title: "CRB Reports",
            items: [
                { text: "Dashboard", to: "/" },
                { text: "CRB Reports", active: true }
            ],
            currentPage: 1,
            perPage: 10,
            pageOptions: [10, 50, 100],
            filter: null,
            isBusy: true,
            selectedReport: null,
            
            name_hover: false,
            current_index: null,


            fields: [
                { key: 'index', label: '#' },
                { key: 'member_name', label: 'Member Name', sortable: true },
                { key: 'reference', label: 'Reference No.', sortable: true },
                { key: 'report_type', label: 'Report Type', sortable: true },
                { key: 'report_date', label: 'Report Date', sortable: true },
                { key: 'generated_by', label: 'Generated By', sortable: true },
                { key: 'actions', label: 'Actions', }
            ]
        };
    },
    computed: {
        ...crbReportsComputed, //[cite: 112]
        totalRows() {
            return this.crbreports.length; //[cite: 111]
        }
    },
    async mounted() {
        this.isBusy = true;
        await this.fetchCrbReports();
        this.isBusy = false;
    },
    methods: {
        ...crbReportsMethods, //[cite: 112]

        promptViewReport(report) {
            this.selectedReport = report;
            this.$bvModal.show('token-warning-modal');
        },

        proceedToViewReport() {
            this.$bvModal.hide('token-warning-modal');

            // Route to your report viewing page or open the actual report modal
            // this.$router.push(`/crb-reports/${this.selectedReport.id}`);

            this.$toast.success('Loading report data for ' + this.selectedReport.reference);
        }
    }
};
</script>

<style scoped>
.learn-more-link {
    cursor: pointer;
    text-decoration: none;
    transition: text-decoration 0.2s ease-in-out;
}

.learn-more-link:hover {
    text-decoration: underline !important;
}
</style>